import { ActionTree } from "vuex";
import { RootState } from "../types";
import { MetadataState, MetadataObjectType, MetadataType, MetadataRequest, NewMetadataItem, Metadata } from "./types";
import metadataApi from "@/services/api.metadata";

export const actions: ActionTree<MetadataState, RootState> = {
	/**
	 * Loads the Metadata and Metadata Object types.
	 */
	async loadMetadataTypes({ commit }) {
		const metadataTypes = await metadataApi.getMetadataTypes();
		const objectTypes = await metadataApi.getObjectTypes();

		commit("setMetadataTypes", metadataTypes);
		commit("setObjectTypes", objectTypes);
	},

	/**
	 * Loads Metadata for a set of objects.
	 */
	async loadMetadata({ commit, getters }, metadataRequest: MetadataRequest) {
		const metadataTypeId = metadataRequest.metadataType
			? getters.getMetadataType(metadataRequest.metadataType)?.metadataTypeId
			: null;

		const apiRequest = {
			metadataTypeId,
			objects: metadataRequest.objects
				.map(object => {
					const objectTypeId = getters.getObjectType(object.objectType)?.objectTypeId;

					if (objectTypeId == null) return null;

					return {
						objectId: object.objectId,
						objectTypeId
					};
				})
				.filter(obj => obj != null)
		};

		if (apiRequest.objects.length == 0) {
			return;
		}

		const metadata = await metadataApi.getMetadataForObjects(apiRequest);

		commit("setMetadata", { metadata, pruneEntries: true });
	},

	/**
	 * Creates a new Metadata item related to an object, adds it to the DB, and then updates the store.
	 */
	async createMetadata({ commit, getters, dispatch }, newMetadata: NewMetadataItem) {
		const metadataTypeId = getters.getMetadataType(newMetadata.metadataType).metadataTypeId;
		const objectTypeId = getters.getObjectType(newMetadata.objectType).objectTypeId;

		const metadata = await metadataApi.createMetadata({
			objectId: newMetadata.objectId,
			objectTypeId,
			metadataTypeId,
			data: JSON.stringify(newMetadata.data)
		});

		// Add the new item of metadata to the store
		commit("setMetadata", { metadata: [metadata], pruneEntries: false });
	},

	/**
	 * Deletes an item of Metadata from the DB, and updates the store if the operation
	 * is successful.
	 */
	async deleteMetadata({ commit }, metadataToDelete: Metadata) {
		await metadataApi.deleteMetadata({
			metadataId: metadataToDelete.metadataId
		});

		const { metadataId, objectId, objectType, metadataType } = metadataToDelete;

		// Once we've deleted the metadata on the server-side, remove it from the store
		commit("deleteMetadata", {
			metadataId,
			objectId,
			objectTypeId: objectType.objectTypeId,
			metadataTypeId: metadataType.metadataTypeId
		});
	}
};
